/**
 * Maps sidebar menu permission keys to role_name values stored in the roles table.
 * Position & Roles assigns real role_name rows — navigation must accept all equivalents.
 */

export const normalizeRoleName = (v) =>
  String(v || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

/** Menu permission → role_name values that unlock that menu item */
export const MENU_PERMISSION_ALIASES = {
  RECRUITMENT: [
    'RECRUITMENT',
    'RECRUITMENT VACANCIES',
    'RECRUITMENT APPLICATIONS',
    'RECRUITMENT SHORTLISTING',
    'RECRUITMENT INTERVIEWS',
    'RECRUITMENT SELECTION',
    'RECRUITMENT OFFERS',
    'VACANCY MANAGEMENT',
    'SHORTLISTING MANAGEMENT',
    'INTERVIEW MANAGEMENT',
    'SELECTION MANAGEMENT',
    'OFFER MANAGEMENT',
    'APPOINTMENT MANAGEMENT',
  ],
  'POSITION & ROLES': ['POSITION & ROLES', 'POSITION MANAGEMENT'],
  'POSITION MANAGEMENT': ['POSITION MANAGEMENT', 'POSITION & ROLES'],
  'SELF ASSESSMENT': ['SELF ASSESSMENT', 'EMPLOYEE SELF ASSESSMENT'],
  'EMPLOYEE SELF ASSESSMENT': ['EMPLOYEE SELF ASSESSMENT', 'SELF ASSESSMENT'],
  'MY APPRAISALS': ['MY APPRAISALS'],
  'MANAGE WORK LOCATIONS': ['MANAGE WORK LOCATIONS', 'MANAGE ATTENDANCE'],
  'MANAGE ATTENDANCE': ['MANAGE ATTENDANCE', 'MANAGE WORK LOCATIONS'],
};

/**
 * Parent/module roles assigned on a position also unlock their child menu permissions.
 * e.g. REPORTS unlocks DAILY ATTENDANCE, MONTHLY ATTENDANCE, etc.
 */
export const PARENT_ROLE_GRANTS = {
  REPORTS: [
    'DAILY ATTENDANCE',
    'MONTHLY ATTENDANCE',
    'DETAILED MONTHLY ATTENDANCE',
    'HR REPORT',
    'LEAVE REPORT',
    'TIMESHEET REPORT',
    'AWOL REPORT',
    'OFF DUTY DETAILS',
  ],
  'LEAVE AND ATTENDANCE': [
    'TIMESHEET',
    'MANAGE ATTENDANCE',
    'MANAGE LEAVE',
    'MANAGE WORK LOCATIONS',
    'REMOTE ATTENDANCE APPROVALS',
  ],
  'EMPLOYEE PERFORMANCE': [
    'MY APPRAISALS',
    'EMPLOYEE SELF ASSESSMENT',
    'SELF ASSESSMENT',
    'MANAGER DASHBOARD',
    'HR DASHBOARD',
    'PERFORMANCE REPORTS',
    'APPRAISAL CYCLES',
    'KPI MANAGEMENT',
    'PIP MANAGEMENT',
    'WARNINGS',
  ],
  'PAYROLL RECONCILIATION': [
    'PAYROLL SETUP',
    'PAYROLL SETUP & CONFIGURATION',
    'PAYROLL PERIODS',
    'PAYROLL RUN MANAGEMENT',
    'PAYROLL PAYMENT',
    'UPLOAD PAYROLL',
    'RECON HISTORY',
  ],
  'PAYROLL SETUP & CONFIGURATION': [
    'PAYROLL SETUP',
    'PAYROLL PERIODS',
    'PAYROLL RECONCILIATION',
  ],
  'PAYROLL RUN MANAGEMENT': [
    'PAYROLL PERIODS',
    'PAYROLL RECONCILIATION',
    'PAYROLL PAYMENT',
  ],
  IMPREST: [
    'IMPREST REQUEST',
    'IMPREST APPROVAL',
    'IMPREST RECONCILIATION',
    'IMPREST HISTORY',
  ],
  HOLIDAYS: [
    'MANAGE HOLIDAYS',
    'HOLIDAY CATEGORIES',
    'HOLIDAY CALENDAR',
    'HOLIDAY STATISTICS',
  ],
  DOCUMENTS: ['GENERAL DOCUMENTS'],
  'EMPLOYEE APPROVALS': [
    'PENDING APPROVALS NEW EMPLOYEES',
    'PENDING APPROVALS DATA EDITS',
    'REJECTED APPROVALS',
  ],
  'HR ACTIONS': [
    'TRANSFERS',
    'EVENTS',
    'MY EVENTS',
    'MEETINGS',
    'ASSETS',
    'PROMOTIONS',
    'WARNINGS',
    'DISCIPLINARY MANAGEMENT',
    'HR CASE MANAGEMENT',
    'PSYCHOSOCIAL MANAGEMENT',
    'ANNOUNCEMENTS',
  ],
  SEPARATION: ['TERMINATION', 'FNF'],
  'SETTINGS AND SETUPS': [
    'ORG STRUCTURE',
    'POSITION & ROLES',
    'POSITION MANAGEMENT',
    'AUDIT LOGS',
    'USER MANAGEMENT',
    'COMPANY CONFIGURATION',
  ],
  ADMIN: [
    'AUDIT LOGS',
    'USER MANAGEMENT',
    'COMPANY CONFIGURATION',
    'POSITION & ROLES',
    'POSITION MANAGEMENT',
    'ORG STRUCTURE',
  ],
  'MY LEAVE': [
    'APPLY FOR LEAVE',
    'LEAVE HISTORY',
    'LEAVE BALANCE',
    'LEAVE CALENDAR',
  ],
  'MY ATTENDANCE': [
    'ATTENDANCE RECORDS',
    'CLOCK IN OUT',
    'ATTENDANCE SUMMARY',
  ],
  'MY PAYROLL': [
    'VIEW PAYSLIPS',
    'TAX DOCUMENTS',
    'PAYROLL HISTORY',
  ],
  'MY DOCUMENTS': [
    'VIEW DOCUMENTS',
    'UPLOAD DOCUMENTS',
  ],
  'MY PERFORMANCE': [
    'MY APPRAISALS',
    'EMPLOYEE SELF ASSESSMENT',
    'SELF ASSESSMENT',
  ],
};

/**
 * True if a role assigned to the user satisfies a menu/route permission string.
 */
export function roleMatchesMenuPermission(userRoleName, menuPermission) {
  const name = normalizeRoleName(userRoleName);
  const target = normalizeRoleName(menuPermission);
  if (!name || !target) return false;

  if (name === target) return true;
  if (name.startsWith(`${target} `)) return true;
  if (target.startsWith(`${name} `)) return true;

  const aliases = MENU_PERMISSION_ALIASES[target];
  if (aliases && aliases.some((a) => normalizeRoleName(a) === name)) {
    return true;
  }

  const parentGrants = PARENT_ROLE_GRANTS[name];
  if (parentGrants && parentGrants.some((a) => normalizeRoleName(a) === target)) {
    return true;
  }

  return false;
}

export function getUserRoleNamesFromSession() {
  try {
    const raw = sessionStorage.getItem('selectedRoles');
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list
      .map((r) => normalizeRoleName(r?.role_name || r?.roleName || r?.role || ''))
      .filter((n) => n && n !== 'NULL');
  } catch {
    return [];
  }
}

export function userHasMenuPermission(menuPermission) {
  if (!menuPermission) return true;

  const perms = Array.isArray(menuPermission) ? menuPermission : [menuPermission];
  const userRoles = getUserRoleNamesFromSession();
  if (userRoles.length === 0) return false;

  return perms.some((p) => userRoles.some((name) => roleMatchesMenuPermission(name, p)));
}
